import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import {
  collection,
  getDocs,
  query,
  where,
  limit,
  startAfter,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";

const PAGE_SIZE = 5;

const MyReferralJobsPage = () => {
  const { user } = useContext(UserContext);
  const [jobs, setJobs] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [deleteJob, setDeleteJob] = useState(null); // store job selected for deletion
  const [deleting, setDeleting] = useState(false);

  // ---------- Fetch Jobs ----------
  const fetchJobs = async (next = false) => {
    if (loading || !user?.id) return;
    setLoading(true);

    try {
      let q;
      if (next && lastDoc) {
        q = query(
          collection(db, "referralJobs"),
          where("postedById", "==", user.id),
          startAfter(lastDoc),
          limit(PAGE_SIZE)
        );
      } else {
        q = query(
          collection(db, "referralJobs"),
          where("postedById", "==", user.id),
          limit(PAGE_SIZE)
        );
      }

      const snapshot = await getDocs(q);
      const page = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Client-side sort latest first (if createdAt exists)
      page.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

      setHasMore(page.length === PAGE_SIZE);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setJobs((prev) => (next ? [...prev, ...page] : page));
    } catch (err) {
      console.error("Error fetching my referral jobs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ---------- Delete Job ----------
  const confirmDelete = async () => {
    if (!deleteJob) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "referralJobs", deleteJob.id));
      setJobs((prev) => prev.filter((job) => job.id !== deleteJob.id));
      setDeleteJob(null);
    } catch (err) {
      console.error("Error deleting job:", err);
      alert("Failed to delete job. Try again!");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PageWrapper>
      <JobsContainer>
        <Title>My Referral Jobs</Title>

        {/* List */}
        {jobs.map((job) => (
          <JobCard key={job.id}>
            <JobHeader>
              <JobTitle>{job.jobTitle}</JobTitle>
              <CompanyName>{job.companyName}</CompanyName>
            </JobHeader>
            <JobInfo>
              <p>
                <strong>Location:</strong> {job.jobLocation}
              </p>
              <p>
                <strong>Experience:</strong> {job.experience}
              </p>
              <p>
                <strong>Description:</strong> {job.description}
              </p>
              <p>
                <strong>Referral Code:</strong> {job.id}
              </p>
              <PostedBy>
                Posted by <span>{job.postedByName}</span> •{" "}
                {job.createdAt?.toDate
                  ? job.createdAt.toDate().toLocaleString()
                  : "N/A"}
              </PostedBy>
            </JobInfo>
            <DeleteButton onClick={() => setDeleteJob(job)}>
              Delete
            </DeleteButton>
          </JobCard>
        ))}

        {/* Empty state */}
        {!loading && jobs.length === 0 && (
          <EmptyMsg>No referral jobs posted yet.</EmptyMsg>
        )}

        {/* Pagination */}
        {hasMore && (
          <LoadMoreButton onClick={() => fetchJobs(true)} disabled={loading}>
            {loading ? "Loading..." : "Load More"}
          </LoadMoreButton>
        )}
      </JobsContainer>

      {/* Delete Confirmation Modal */}
      {deleteJob && (
        <ModalOverlay>
          <ModalContent>
            <h3>Delete Job</h3>
            <p>
              Are you sure you want to delete{" "}
              <strong>{deleteJob.jobTitle}</strong> at{" "}
              <strong>{deleteJob.companyName}</strong>?
            </p>
            <ModalActions>
              <CancelButton onClick={() => setDeleteJob(null)}>
                Cancel
              </CancelButton>
              <DeleteConfirmButton onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete"}
              </DeleteConfirmButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageWrapper>
  );
};

export default MyReferralJobsPage;

/* ---------------- Styled Components ---------------- */
/* ---------------- Glassy Purple-Themed Styled Components ---------------- */

const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 40px 20px;
  background: radial-gradient(circle at top left, #0b0617, #000);
  display: flex;
  justify-content: center;
`;

const JobsContainer = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const Title = styled.h2`
  text-align: center;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 700;
  margin-bottom: 28px;
`;

const JobCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.15);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
  color: #fff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(14px);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 45px rgba(155, 92, 255, 0.25);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const JobHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const JobTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

const CompanyName = styled.span`
  font-size: 1rem;
  color: #bfbfbf;
`;

const JobInfo = styled.div`
  font-size: 0.95rem;
  line-height: 1.6;
  margin-top: 12px;
  margin-bottom: 14px;

  strong {
    color: #c59fff;
  }

  p {
    margin: 6px 0;
    color: #ddd;
  }
`;

const PostedBy = styled.p`
  font-size: 0.8rem;
  color: #aaa;
  margin-top: 10px;

  span {
    color: #fff;
    font-weight: 600;
  }
`;

const DeleteButton = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(90deg, #ef4444, #dc2626);
  color: #fff;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: 0.25s all ease;
  box-shadow: 0 6px 18px rgba(239, 68, 68, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 36px rgba(239, 68, 68, 0.35);
  }
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 30px;
  font-size: 1rem;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.3s ease;
  box-shadow: 0 6px 18px rgba(155, 92, 255, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 36px rgba(155, 92, 255, 0.35);
  }

  &:disabled {
    background: #555;
    cursor: not-allowed;
  }
`;

const EmptyMsg = styled.p`
  text-align: center;
  color: #999;
  font-size: 1rem;
  padding: 20px 0;
`;

/* ---------------- Modal Styles ---------------- */

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(155, 92, 255, 0.15);
  border-radius: 16px;
  padding: 28px;
  width: 420px;
  max-width: 95%;
  color: #fff;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  animation: fadeIn 0.3s ease;

  h3 {
    font-size: 1.3rem;
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 10px;
  }

  p {
    font-size: 0.95rem;
    color: #ccc;
    margin-bottom: 20px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(155, 92, 255, 0.25);
  color: #fff;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(155, 92, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const DeleteConfirmButton = styled.button`
  flex: 1;
  padding: 12px;
  background: linear-gradient(90deg, #ef4444, #dc2626);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.25s ease;
  box-shadow: 0 6px 18px rgba(239, 68, 68, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 36px rgba(239, 68, 68, 0.35);
  }

  &:disabled {
    background: #555;
    cursor: not-allowed;
  }
`;
